import { type ChangeEvent, useCallback } from "react";
import { useStateWithFallback } from "@tellescope/react-hooks";
import type { SignatureFieldProps } from "./SignatureField";

export const useSignatureFieldLogic = ({ value, onChange, options, fullName, onBlur }: SignatureFieldProps) => {
  const prefillName = options?.prefillSignature ? fullName : "";
  const [consent, setConsent] = useStateWithFallback<boolean>({ fallbackValue: !!value?.signed });
  const [signature, setSignature] = useStateWithFallback<string>({ fallbackValue: value?.fullName ?? "" });

  const notifyParent = useCallback(
    (nextConsent: boolean, nextSignature: string) => {
      onChange({
        ...value,
        signed: nextConsent,
        fullName: nextSignature,
        ...(options?.pdfAttachment ? { pdfAttachment: options.pdfAttachment } : {}),
        ...(options?.signatureUrl ? { url: options.signatureUrl } : {}),
      });
    },
    [onChange, options?.pdfAttachment, options?.signatureUrl, value]
  );

  const handleConsentChange = useCallback(
    (checked: boolean) => {
      // Consenting with nothing typed yet adopts the prefill so the name field is not left blank.
      const nextSignature = checked && !signature ? prefillName : signature;
      setConsent(checked);
      setSignature(nextSignature);
      notifyParent(checked, nextSignature);
    },
    [notifyParent, prefillName, setConsent, setSignature, signature]
  );

  const handleSignatureChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSignature(e.target.value);
      notifyParent(consent, e.target.value);
    },
    [consent, notifyParent, setSignature]
  );

  const handleBlur = useCallback(() => {
    notifyParent(consent, signature);
    onBlur?.();
  }, [consent, notifyParent, onBlur, signature]);

  return {
    consent,
    signature,
    prefillName,
    handleConsentChange,
    handleSignatureChange,
    handleBlur,
  };
};
